import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  HeartPulse,
  Wind,
  Sparkles,
  Compass,
  Hand,
  ArrowRight,
} from "lucide-react";

const items = [
  {
    icon: HeartPulse,
    title: "Therapeutic Movement (Āsana)",
    body: "Gentle, precisely-guided postures that meet your body where it is — supporting mobility, strength, and pain reduction.",
  },
  {
    icon: Wind,
    title: "Prāṇāyāma & Breath",
    body: "Breath practices that regulate Prāṇa — your lifeforce energy — which is essential to balance in body, mind, and health.",
  },
  {
    icon: Sparkles,
    title: "Meditation & Mindfulness",
    body: "Simple, sustainable practices for clarity, emotional regulation, and inner steadiness.",
  },
  {
    icon: Hand,
    title: "Therapeutic Touch",
    body: "Gentle, hands-on support to help release tension, soften the nervous system, and encourage the body's natural repair.",
  },
  {
    icon: Compass,
    title: "Lifestyle Guidance",
    body: "Individualized suggestions for rhythm, rest, and daily habits that support your natural capacity to heal.",
  },
];

const ServicesPreview = () => {
  return (
    <section
      data-testid="services-preview"
      className="bg-[#FDFCFB] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-5">
            <p className="text-xs tracking-[0.35em] uppercase text-[#7B8E76] mb-6">
              Yoga Therapy
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#2D3A2B] leading-tight">
              The tools of yoga, offered with <span className="italic">warmth</span> and care.
            </h2>
          </div>
          <div className="md:col-span-7 flex md:items-end">
            <p className="text-lg text-[#4A5D46] leading-relaxed font-light">
              Every session begins with listening — to your goals, your body,
              and what&rsquo;s asking to be met. From there, we shape a practice
              that supports healing, restores balance, and reconnects you with
              your own steadiness.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {items.map((it, i) => (
            <div
              key={it.title}
              data-testid={`service-card-${i}`}
              className="group bg-[#F4F1ED] p-8 rounded-md border border-transparent hover:border-[#C6A692] transition-all duration-500 hover:-translate-y-1"
            >
              <div className="w-12 h-12 rounded-full bg-[#FDFCFB] flex items-center justify-center mb-6 text-[#4A5D46] group-hover:bg-[#8A9A83] group-hover:text-white transition-colors">
                <it.icon className="w-5 h-5" aria-hidden />
              </div>
              <h3 className="font-serif text-xl text-[#2D3A2B] mb-3 leading-snug">
                {it.title}
              </h3>
              <p className="text-sm text-[#4A5D46] leading-relaxed font-light">
                {it.body}
              </p>
            </div>
          ))}
        </div>

        {/* Lifeforce energy note */}
        <div
          data-testid="lifeforce-note"
          className="mt-14 max-w-3xl mx-auto text-center"
        >
          <p className="font-serif italic text-lg md:text-xl text-[#4A5D46] leading-relaxed">
            Lifeforce energy isn&rsquo;t often spoken about — yet without it,
            we wouldn&rsquo;t be alive. It can be disrupted by trauma, stress,
            not enough movement, hindered breathing, or poor sleep. So much of
            this work is quietly returning that current home.
          </p>
        </div>

        <div className="mt-14 flex justify-center">
          <Link to="/services" data-testid="services-preview-cta">
            <Button
              variant="outline"
              className="rounded-full border-[#2D3A2B] text-[#2D3A2B] hover:bg-[#2D3A2B] hover:text-[#FDFCFB] px-8 py-6 h-auto bg-transparent"
            >
              See the Full Yoga Therapy Approach
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesPreview;
